import { ChartConfig, ChartData, ChartDataset } from '@components/svg-chart';
import { AssignmentAuditDetailsResponse } from '@6grain/masofa-monolith-api-client/common/model/assignmentAuditDetailsResponse';
import { AssignmentStorageRecordGroupResponse } from '@6grain/masofa-monolith-api-client/common/model/assignmentStorageRecordGroupResponse';
import { AssignmentStorageRecordResponse } from '@6grain/masofa-monolith-api-client/common/model/assignmentStorageRecordResponse';
import { ObjectStorageBucketSnapshot } from '@6grain/masofa-monolith-api-client/common/model/objectStorageBucketSnapshot';
import { ObjectStorageBucketWriter } from '@6grain/masofa-monolith-api-client/common/model/objectStorageBucketWriter';
import { ObjectStorageSnapshot } from '@6grain/masofa-monolith-api-client/common/model/objectStorageSnapshot';
import { ObjectStorageWriteSeries } from '@6grain/masofa-monolith-api-client/common/model/objectStorageWriteSeries';
import { formatDatabaseSizeBytes } from '@app/pages/admin/orchestration-database-connections.util';
import { environment } from '@root/src/environments/environment';

export type {
  ObjectStorageBucketSnapshot,
  ObjectStorageBucketWriter,
  ObjectStorageSnapshot,
  ObjectStorageWriteSeries,
};

export const OBJECT_STORAGE_WRITE_WINDOWS = ['24h', '7d'] as const;
export type ObjectStorageWriteWindow = (typeof OBJECT_STORAGE_WRITE_WINDOWS)[number];

export interface ProcessStorageEffect {
  command: string;
  bucket: string;
  objectKind: string;
  operation: string;
  description: string;
}

export interface AssignmentStorageRecord {
  id: string;
  path: string | null;
  objectKind: string | null;
  bytes: number;
  createdAt: string;
}

export interface AssignmentStorageRecordGroup {
  bucket: string;
  objectCount: number;
  bytesWritten: number;
  items: AssignmentStorageRecord[];
}

const MIB = 1024 * 1024;
const CHART_COLORS = [
  '#2563eb',
  '#16a34a',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#4f46e5',
  '#65a30d',
  '#ea580c',
];

export function parseObjectStorageWriteWindow(value: string | null | undefined): ObjectStorageWriteWindow {
  return value === '7d' ? '7d' : '24h';
}

export function objectStorageWriteWindowBounds(
  window: ObjectStorageWriteWindow,
  now = new Date(),
): { from: string; to: string } {
  const to = now.toISOString();
  const fromDate = new Date(now.getTime());
  fromDate.setTime(fromDate.getTime() - (window === '7d' ? 7 : 1) * 24 * 60 * 60 * 1000);
  return { from: fromDate.toISOString(), to };
}

export function normalizeProcessStorageEffects(value: unknown): ProcessStorageEffect[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => (item && typeof item === 'object' ? item as Record<string, unknown> : null))
    .filter((item): item is Record<string, unknown> => !!item)
    .map(item => ({
      command: stringOrEmpty(item['command']) || '*',
      bucket: stringOrEmpty(item['bucket']),
      objectKind: stringOrEmpty(item['objectKind']) || 'Other',
      operation: stringOrEmpty(item['operation']),
      description: stringOrEmpty(item['description']),
    }))
    .filter(item => !!item.bucket && !!item.operation);
}

export function readAssignmentStorageRecords(
  assignment: Pick<AssignmentAuditDetailsResponse, 'storageRecords'> | null | undefined,
): AssignmentStorageRecordGroup[] {
  const value = assignment?.storageRecords;
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => normalizeStorageGroup(item))
    .filter((item): item is AssignmentStorageRecordGroup => !!item);
}

/** Downloads the catalogued object bytes from S3 through FileStorageItem/GetFile. */
export function fileStorageDownloadUrl(id: string | null | undefined): string | null {
  const value = (id ?? '').trim();
  return value ? `${environment.api}/common/FileStorageItem/GetFile/${value}` : null;
}

export function flattenAssignmentStorageRecords(
  groups: AssignmentStorageRecordGroup[],
): Array<AssignmentStorageRecord & { bucket: string; bytesLabel: string; downloadUrl: string | null }> {
  return groups.flatMap(group => group.items.map(item => ({
    ...item,
    bucket: group.bucket,
    bytesLabel: formatDatabaseSizeBytes(item.bytes),
    downloadUrl: fileStorageDownloadUrl(item.id),
  })));
}

export function assignmentStorageBytesLabel(groups: AssignmentStorageRecordGroup[]): string {
  return formatDatabaseSizeBytes(groups.reduce((sum, group) => sum + group.bytesWritten, 0));
}

export function objectStorageBucketSearchFields(row: ObjectStorageBucketSnapshot): Array<string | null | undefined> {
  return [
    row.name,
    formatDatabaseSizeBytes(row.sizeBytes),
    row.objectCount == null ? undefined : String(row.objectCount),
    ...(row.writers ?? []).flatMap(writer => [writer.processKey, writer.displayName, writer.command]),
  ];
}

export function objectStorageBucketCount(snapshot: ObjectStorageSnapshot | null | undefined): number {
  return snapshot?.buckets?.length ?? 0;
}

export function objectStorageLatestWriteAt(snapshot: ObjectStorageSnapshot | null | undefined): string | null {
  const times = (snapshot?.buckets ?? [])
    .map(bucket => bucket.lastWriteAt)
    .filter((value): value is string => !!value)
    .sort();
  return times.at(-1) ?? null;
}

export function objectStorageWritersLabel(writers: ObjectStorageBucketWriter[] | null | undefined): string {
  const keys = (writers ?? [])
    .map(writer => writer.processKey?.trim())
    .filter((key): key is string => !!key);
  return [...new Set(keys)].join(', ');
}

export function buildObjectStorageWriteChart(
  series: ObjectStorageWriteSeries | null | undefined,
  visibleBuckets: string[],
): ChartData {
  const allowed = new Set(visibleBuckets.filter(Boolean));
  const points = (series?.points ?? []).filter(point => point.bucket && allowed.has(point.bucket));
  const labels = [...new Set(points
    .map(point => point.periodStart)
    .filter((value): value is string => !!value))]
    .sort();
  const buckets = [...new Set(points.map(point => point.bucket).filter((value): value is string => !!value))]
    .sort((left, right) => left.localeCompare(right));
  const datasets: ChartDataset[] = buckets.map((bucket, index) => {
    const color = CHART_COLORS[index % CHART_COLORS.length];
    const byPeriod = new Map(
      points
        .filter(point => point.bucket === bucket)
        .map(point => [point.periodStart, (point.bytesWritten ?? 0) / MIB]),
    );
    return {
      label: bucket,
      data: labels.map(label => byPeriod.get(label) ?? 0),
      borderColor: color,
      backgroundColor: color,
    };
  });
  return {
    labels: labels.map(formatChartPeriodLabel),
    datasets,
  };
}

export function objectStorageWriteChartConfig(): ChartConfig {
  return {
    yAxisUnit: 'MiB',
    scales: {
      x: { stacked: true },
      y: { stacked: true, beginAtZero: true },
    },
  };
}

function normalizeStorageGroup(
  item: AssignmentStorageRecordGroupResponse | null | undefined,
): AssignmentStorageRecordGroup | null {
  const items = normalizeStorageItems(item?.items);
  const objectCount = typeof item?.objectCount === 'number' ? item.objectCount : items.length;
  const bytesWritten = typeof item?.bytesWritten === 'number'
    ? item.bytesWritten
    : items.reduce((sum, row) => sum + row.bytes, 0);
  const bucket = stringOrEmpty(item?.bucket);
  if (!bucket || objectCount <= 0) {
    return null;
  }
  return { bucket, objectCount, bytesWritten, items };
}

function normalizeStorageItems(
  value: AssignmentStorageRecordResponse[] | null | undefined,
): AssignmentStorageRecord[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => ({
      id: stringOrEmpty(item.id),
      path: stringOrNull(item.path),
      objectKind: stringOrNull(item.objectKind),
      bytes: typeof item.bytes === 'number' ? item.bytes : 0,
      createdAt: stringOrEmpty(item.createdAt),
    }))
    .filter(item => !!item.id);
}

function formatChartPeriodLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function stringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function stringOrNull(value: unknown): string | null {
  const text = stringOrEmpty(value);
  return text || null;
}
